/**
 * LiveClassArchiveListPage — library-style archive browser.
 *
 * Layout goals:
 *  - Left sidebar: facets for Functions (grade-scoped) + Hosts. Multi-select ANDs.
 *  - Main pane: cards grouped by month; last 2 months always visible,
 *    older months collapsed into accordions.
 *  - Full-text search across titles, summaries and Q&A (backed by
 *    `/api/live-classes/past?q=...` which already searches all three).
 *  - Mobile: sidebar collapses into a slide-in Sheet drawer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { usePreferences } from '../context/PreferencesContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  ArrowLeft, Loader2, Archive, AlertCircle, Search, Lock,
  SlidersHorizontal, ChevronDown, ChevronRight, X,
} from 'lucide-react';
import { ArchiveCard } from '../components/live-classes/ArchiveCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// -------------------- Helpers --------------------
const monthKey = (iso) => {
  // Returns { key: '2026-02', label: 'Feb 2026' } from an ISO date.
  if (!iso) return { key: 'unknown', label: 'Undated' };
  try {
    const d = new Date(iso);
    if (isNaN(d)) return { key: 'unknown', label: 'Undated' };
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', month: 'short', year: 'numeric',
    });
    return { key, label };
  } catch {
    return { key: 'unknown', label: 'Undated' };
  }
};

const groupByMonth = (classes) => {
  // Returns an array of { key, label, classes[] } sorted newest month first.
  const map = new Map();
  for (const c of classes) {
    const { key, label } = monthKey(c.start_time);
    if (!map.has(key)) map.set(key, { key, label, classes: [] });
    map.get(key).classes.push(c);
  }
  return Array.from(map.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
};

// -------------------- Facet list (sidebar) --------------------
const FacetSection = ({ title, options, selected, onToggle, testIdPrefix, colorAccent }) => {
  if (options.length === 0) return null;
  return (
    <div className="mb-5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</p>
      <div className="space-y-1">
        {options.map((opt) => {
          const active = selected.includes(opt.id);
          const fullText = opt.prefix ? `${opt.prefix} ${opt.label}` : opt.label;
          return (
            <button
              key={opt.id}
              onClick={() => onToggle(opt.id)}
              title={fullText}
              className={`w-full flex items-start justify-between gap-2 text-left px-2 py-1.5 rounded-md text-sm transition-colors ${
                active
                  ? `${colorAccent.active} font-medium`
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`${testIdPrefix}-${opt.id}`}
            >
              <span className="flex-1 leading-snug break-words">
                {opt.prefix && <span className="opacity-60 mr-1 text-xs">{opt.prefix}</span>}
                {opt.label}
              </span>
              <span className={`text-xs tabular-nums shrink-0 mt-0.5 ${active ? '' : 'text-gray-400'}`}>{opt.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// -------------------- Sidebar body (used in both desktop rail + mobile Sheet) --------------------
const SidebarBody = ({
  functionGroups,
  selectedFunctions, toggleFunction,
  onClearAll,
}) => {
  const anySelected = selectedFunctions.length > 0;
  const totalOptions = functionGroups.orals.length + functionGroups.written.length;
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Functions</h3>
        {anySelected && (
          <button
            onClick={onClearAll}
            className="text-xs text-[#0A7EA4] hover:underline"
            data-testid="archive-library-clear-all"
          >
            Clear all
          </button>
        )}
      </div>
      {totalOptions === 0 ? (
        <p className="text-xs text-gray-400 italic">Function filters will appear once tagged classes are archived for your grade.</p>
      ) : (
        <>
          <FacetSection
            title="Orals"
            options={functionGroups.orals}
            selected={selectedFunctions}
            onToggle={toggleFunction}
            testIdPrefix="archive-facet-function"
            colorAccent={{ active: 'bg-[#0A7EA4]/10 text-[#0A7EA4]' }}
          />
          <FacetSection
            title="Written"
            options={functionGroups.written}
            selected={selectedFunctions}
            onToggle={toggleFunction}
            testIdPrefix="archive-facet-function"
            colorAccent={{ active: 'bg-purple-100 text-purple-800' }}
          />
        </>
      )}
    </div>
  );
};

// -------------------- Month group renderer --------------------
const MonthGroup = ({ label, classes, navigate, fnById, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-6" data-testid={`archive-month-group-${label.replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 mb-3 group"
        data-testid={`archive-month-toggle-${label.replace(/\s+/g, '-')}`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />}
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700">{label}</h2>
        <span className="text-xs text-gray-400 tabular-nums">({classes.length})</span>
      </button>
      {open && (
        <div className="grid gap-3 md:grid-cols-2">
          {classes.map((cls) => (
            <ArchiveCard key={cls.id} cls={cls} navigate={navigate} fnById={fnById} from="/live-classes/archive" />
          ))}
        </div>
      )}
    </section>
  );
};

// -------------------- Page --------------------
const LiveClassArchiveListPage = () => {
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const userGrade = preferences?.grade;

  const { canAccessLiveClasses } = useSubscriptionAccess();
  const { getLiveClassesStatusForGrade, getModuleStatus } = useModuleAccess();
  const gradeStatus = userGrade && getLiveClassesStatusForGrade ? getLiveClassesStatusForGrade(userGrade) : null;
  const parentStatus = getModuleStatus ? getModuleStatus('live_classes') : null;
  const moduleStatus = gradeStatus ?? parentStatus;
  const isLocked = moduleStatus !== 'hidden' && !canAccessLiveClasses();

  useEffect(() => {
    if (moduleStatus === 'hidden') navigate('/', { replace: true });
  }, [moduleStatus, navigate]);

  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [functionCatalog, setFunctionCatalog] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Debounce search 300ms to avoid hammering the endpoint on each keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadFunctions = useCallback(async () => {
    if (!userGrade) { setFunctionCatalog([]); return; }
    try {
      const { data } = await axios.get(`${API}/functions`, { params: { grade: userGrade } });
      setFunctionCatalog(Array.isArray(data) ? data : (data.functions || []));
    } catch (_) { /* silent */ }
  }, [userGrade]);

  const loadPast = useCallback(async (searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      const params = searchTerm ? { q: searchTerm } : {};
      const { data } = await axios.get(`${API}/live-classes/past`, { params });
      setPast(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response?.data?.detail || 'You need a Live Classes subscription to browse the archive.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load class archive');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLocked || moduleStatus === 'hidden') return;
    loadFunctions();
  }, [loadFunctions, isLocked, moduleStatus]);

  useEffect(() => {
    if (isLocked || moduleStatus === 'hidden') return;
    loadPast(debouncedSearch);
  }, [loadPast, debouncedSearch, isLocked, moduleStatus]);

  const fnById = useCallback(
    (id) => functionCatalog.find((fn) => fn.id === id) || null,
    [functionCatalog]
  );

  // Facet options — function counts computed against the current search
  // window. Zero-count functions are hidden to reduce sidebar noise.
  // Split by exam_type so the sidebar can group them under Orals / Written
  // sub-headings, matching the pattern used in LiveClassesUserPage.
  const functionGroups = useMemo(() => {
    const orals = [];
    const written = [];
    for (const fn of functionCatalog) {
      const count = past.filter((c) => Array.isArray(c.functions) && c.functions.includes(fn.id)).length;
      if (count === 0) continue;
      const opt = {
        id: fn.id,
        label: fn.name,
        prefix: `F${fn.function_number}`,
        count,
      };
      const t = (fn.exam_type || '').toLowerCase();
      if (t === 'written') written.push(opt);
      else orals.push(opt);  // default (orals or missing) → orals bucket
    }
    const byPrefix = (a, b) => (a.prefix > b.prefix ? 1 : -1);
    return { orals: orals.sort(byPrefix), written: written.sort(byPrefix) };
  }, [functionCatalog, past]);

  const filteredPast = useMemo(() => {
    let list = past;
    if (selectedFunctions.length > 0) {
      const wanted = new Set(selectedFunctions);
      list = list.filter((c) => Array.isArray(c.functions) && c.functions.some((f) => wanted.has(f)));
    }
    return list;
  }, [past, selectedFunctions]);

  // Within-month sort: highest Q&A count first (surfaces rich discussion
  // classes at the top of each month for free — no extra UI). Ties fall
  // back to start_time DESC.
  const monthGroups = useMemo(() => {
    const groups = groupByMonth(filteredPast);
    return groups.map((g) => ({
      ...g,
      classes: [...g.classes].sort((a, b) => {
        const qa = (b.qna_count || 0) - (a.qna_count || 0);
        if (qa !== 0) return qa;
        return (b.start_time || '').localeCompare(a.start_time || '');
      }),
    }));
  }, [filteredPast]);

  const toggleFunction = (id) =>
    setSelectedFunctions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const clearAllFilters = () => {
    setSelectedFunctions([]);
  };

  const activeFilterCount = selectedFunctions.length;

  // ---------- Splash states ----------
  if (moduleStatus === 'hidden') {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (isLocked) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 max-w-3xl">
          <Card className="border-[#FF6B6B]/25 bg-[#FF6B6B]/5" data-testid="archive-locked-splash">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FF6B6B]/15 flex items-center justify-center shrink-0">
                  <Lock className="w-6 h-6 text-[#FF6B6B]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">Class Archive is a Live Classes benefit</h2>
                  <p className="text-sm text-gray-700 mb-4">
                    Recordings, summaries and Q&amp;A of every past class are available with a Live Classes plan.
                    Never miss a session again — replay any class on your own time.
                  </p>
                  <Button onClick={() => navigate('/plans?highlight=live_classes')} className="bg-[#FF6B6B] hover:bg-[#e05a5a]" data-testid="archive-upgrade-btn">
                    View plans
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // ---------- Main layout ----------
  const sidebarProps = {
    functionGroups,
    selectedFunctions, toggleFunction,
    onClearAll: clearAllFilters,
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Back link */}
        <div className="flex items-center gap-2 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-gray-600" data-testid="archive-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>

        {/* Header + search bar */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-100">
              <Archive className="w-7 h-7 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Class Archive</h1>
              <p className="text-sm text-gray-600">Watch, read and revisit any past live session for your grade.</p>
            </div>
          </div>
          <div className="relative w-full sm:w-96">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Search titles, topics, questions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 text-sm"
              data-testid="archive-list-search-input"
            />
          </div>
        </div>

        {/* Mobile "Filters" button (hidden on md+) */}
        <div className="md:hidden mb-4">
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="archive-mobile-filters-btn">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center bg-[#0A7EA4] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetHeader className="px-4 pt-4 pb-0">
                <SheetTitle className="text-base">Functions</SheetTitle>
              </SheetHeader>
              <SidebarBody {...sidebarProps} />
            </SheetContent>
          </Sheet>
        </div>

        {!userGrade ? (
          <Card className="border-[#0A7EA4]/25 bg-[#0A7EA4]/5" data-testid="archive-no-grade">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#0A7EA4] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 mb-1">Pick your grade to see the archive</p>
                <p className="text-sm text-gray-700">Archives are grade-specific — set your grade so we can show the ones relevant to you.</p>
                <Button onClick={() => navigate('/profile')} className="mt-3 bg-[#0A7EA4] hover:bg-[#086684]" size="sm" data-testid="archive-set-grade-btn">
                  Set your grade
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">Couldn&apos;t load archive</p>
                <p className="text-sm text-amber-800">{error}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="flex gap-6">
            {/* Desktop sidebar (hidden on mobile) */}
            <aside className="hidden md:block w-60 shrink-0 sticky top-4 self-start" data-testid="archive-library-sidebar">
              <div className="rounded-lg border bg-gray-50/60 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <SidebarBody {...sidebarProps} />
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {/* Result count + active filter chips */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <p className="text-xs text-gray-500 tabular-nums">
                  {filteredPast.length}{activeFilterCount > 0 ? ` of ${past.length}` : ''} archived {filteredPast.length === 1 ? 'class' : 'classes'}
                </p>
                {selectedFunctions.map((fid) => {
                  const fn = fnById(fid);
                  if (!fn) return null;
                  return (
                    <button
                      key={`fnchip-${fid}`}
                      onClick={() => toggleFunction(fid)}
                      className="inline-flex items-center gap-1 text-xs bg-[#0A7EA4]/10 text-[#0A7EA4] px-2 py-0.5 rounded-full hover:bg-[#0A7EA4]/20"
                      data-testid={`archive-active-fn-${fid}`}
                    >
                      F{fn.function_number} · {fn.name}
                      <X className="w-3 h-3" />
                    </button>
                  );
                })}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : monthGroups.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500" data-testid="archive-list-empty">
                    <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">
                      {activeFilterCount > 0
                        ? 'No archived classes match the current filters'
                        : debouncedSearch ? 'No archived classes match your search' : 'No archived classes yet'}
                    </h3>
                    <p className="text-sm">
                      {activeFilterCount > 0
                        ? 'Try removing a filter or clear all.'
                        : debouncedSearch
                          ? 'Try a different keyword or clear the search.'
                          : 'Completed classes will appear here once the faculty publishes the recording and summary.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                monthGroups.map((g, idx) => (
                  <MonthGroup
                    key={g.key}
                    label={g.label}
                    classes={g.classes}
                    navigate={navigate}
                    fnById={fnById}
                    defaultOpen={idx < 2}
                  />
                ))
              )}
            </main>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LiveClassArchiveListPage;
